<template>
  <div class="flex flex-col h-full bg-white divide-y divide-gray-200 shadow-xl">
    <div class="flex flex-col flex-1 min-h-0 py-6 overflow-y-scroll">
      <div class="px-4 sm:px-6">
        <div class="flex items-start justify-between">
          <DialogTitle class="text-base font-semibold leading-6 text-gray-900">
            {{ translatedTitle }}
          </DialogTitle>
          <div class="flex items-center ml-3 h-7">
            <button
              type="button"
              class="text-gray-400 bg-white rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click="$emit('cancel')">
              <span class="sr-only">Close panel</span>
              <X class="w-6 h-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <div class="relative flex-1 px-4 mt-6 sm:px-6">
        <TextInput
          v-if="users.length"
          v-model="query"
          label=""
          name="name"
          type="text"
          :validate-on-input="true"
          classes="w-full p-2 "
          :placeholder="$t('Search team members by email or name')" />
        <div class="pt-2">
          <div v-if="filtered_users.length">
            <ul role="list" class="flex-1 divide-y divide-gray-200 dark:divide-gray-500">
              <li v-for="user in filtered_users" :key="user.email" class="relative cursor-pointer">
                <div class="relative flex items-center px-5 py-5 pt-6 group">
                  <div class="flex-1 block p-1 -m-1">
                    <div class="absolute inset-0 group-hover:bg-gray-100 dark:group-hover:bg-dark-divider" aria-hidden="true" />
                    <div class="relative flex items-center flex-1 min-w-0">
                      <span class="relative flex-shrink-0 inline-block">
                        <img v-if="user.avatar" class="inline-block w-10 h-10 rounded-full" :src="user.avatar" alt="">
                        <Avatar v-else :first-name="user.first_name" :last-name="user.last_name" />
                      </span>
                      <div class="ml-4 truncate">
                        <p class="text-sm font-medium text-gray-900 truncate dark:text-white">
                          {{ Record.from(user).full_name }}
                        </p>
                        <p class="text-sm text-gray-500 truncate">
                          {{ Record.from(user).email.split("@")[0] }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div as="div" class="absolute top-0 right-0 z-10 flex-shrink-0 inline-block py-2 ml-2 text-left">
                    <slot name="actions">
                      <div class="flex flex-col items-stretch justify-end gap-1">
                        <select
                          id="access"
                          v-model="user.access"
                          name="access"
                          class="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
                          @change="toggleUser($event, user)">
                          <option
                            v-for="item in [
                              { name: $t('Not Shared'), value: 'no-access' },
                              { name: $t('Admin'), value: 'admin' },
                              { name: $t('Editor'), value: 'write' },
                              { name: $t('Viewer'), value: 'read' }
                            ]"
                            :key="item.value"
                            :class="{ bold: user.access != 'no-access' }"
                            selected="no-access"
                            :value="item.value">
                            {{ item.name }}
                          </option>
                        </select>
                        <div
                          v-if="user.access != 'no-access'"
                          class="relative flex flex-row items-end justify-end w-full gap-1 text-sm font-light text-right group">
                          <span class="font-bold whitespace-nowrap dark:text-common-grey">{{
                            user.is_shared_as_template ? $t("Layout") : $t("Data and Layout")
                          }}</span>
                          <Switch
                            v-model="user.is_shared_as_template"
                            :class="[
                              user.is_shared_as_template ? 'bg-blue opacity-70' : 'bg-blue',
                              ' relative inline-flex flex-shrink-0 h-5 w-10 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                            ]"
                            @update:model-value="toggleUser($event, user)">
                            <span
                              aria-hidden="true"
                              :class="[
                                user.is_shared_as_template ? 'translate-x-0' : 'translate-x-5',
                                'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                              ]" />
                          </Switch>
                        </div>
                      </div>
                    </slot>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div v-if="!filtered_users.length" class="flex flex-col items-center justify-center py-12 text-center">
            <Users class="w-12 h-12 text-gray-300" aria-hidden="true" />
            <h3 class="mt-4 text-sm font-medium text-gray-900">
              {{ !users.length ? $t("No team members") : $t("No matching results") }}
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              {{ !users.length ? $t("Add team members to start collaborating") : $t("Try adjusting your search terms") }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end flex-shrink-0 px-4 py-4">
      <button type="button" class="w-1/2 m-0 cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
      <button type="submit" class="w-1/2 primary-btn" @click="submit">
        {{ translatedSubmitText }}
      </button>
    </div>
  </div>
</template>

<script>
import { DialogTitle } from "@headlessui/vue"
import Avatar from "./Avatar.vue"
import TextInput from "./TextInput.vue"
import { Record } from "@/models/record"
import { X, Users } from "lucide-vue-next"
import { Switch } from "@headlessui/vue"
import { utils } from "@/store/helpers"
export default {
  components: {
    Users,
    Switch,
    DialogTitle,
    X,
    Avatar,
    TextInput
  },
  props: {
    title: {
      type: String,
      default: "Change Co-Owners"
    },
    submitText: {
      type: String,
      default: "Submit"
    },
    selectedUsers: {
      type: Array,
      default: () => []
    }
  },
  emits: ["cancel", "submit", "update:deselectedUsers", "update:selectedUsers", "close"],
  data() {
    return {
      Record,
      query: null,
      users: [],
      assigned_users: [],
      unassigned_users: []
    }
  },
  computed: {
    translatedTitle() {
      return this.$t(this.title)
    },
    translatedSubmitText() {
      return this.$t(this.submitText)
    },
    filtered_users() {
      if (this.query) {
        return this.users.filter(
          (u) => u["email"].toLowerCase().search(this.query) != -1 || u["full_name"].toLowerCase().search(this.query) != -1
        )
      } else {
        return this.users
      }
    }
  },

  created() {
    this.users = this.$store.state.data.users
      .filter((u) => u.id != this.$store.state.auth.user.id)
      .map((u) => {
        u["is_checked"] = this.selectedUsers.findIndex((x) => x.id == u.id) != -1
        u["access"] = this.selectedUsers.find((x) => x.id == u.id)?.access || "no-access"
        u["is_shared_as_template"] = this.selectedUsers.find((x) => x.id == u.id)?.is_shared_as_template == true || false
        return u
      })
    this.assigned_users = this.users.filter((u) => u.is_checked)
    this.$emit("update:selectedUsers", this.assigned_users)
  },

  methods: {
    toggleUser(e, user) {
      user.is_checked = user.access != "no-access"
      if (user.is_checked) {
        this.unassigned_users = this.unassigned_users.filter((u) => u.id != user.id)
        utils.replaceOrAddItem(this.assigned_users, {
          id: user.id,
          access: user.access,
          is_shared_as_template: user?.is_shared_as_template == true || false
        })
      } else {
        this.assigned_users = this.assigned_users.filter((u) => u.id != user.id)
        utils.replaceOrAddItem(this.unassigned_users, {
          id: user.id,
          access: user.access,
          is_shared_as_template: user?.is_shared_as_template == true || false
        })
      }

      this.$emit("update:selectedUsers", this.assigned_users)
      this.$emit("update:deselectedUsers", this.unassigned_users)
    },
    submit() {
      this.$emit(
        "submit",
        this.users.filter((u) => u.is_checked)
      )
    }
  }
}
</script>
